import { z } from 'zod';
import { cuid, paginacao } from './primitives.js';
import { dadosParceiroAtualizarShape, dadosParceiroShape, documentoParceiroSchema, naturezaParceiroSchema, umPrincipal, type DadosParceiroResponse } from './parceiros.js';

/** CPF (11) ou CNPJ (14) so com digitos e digito verificador correto (Sessao 30). */
const documento = documentoParceiroSchema;

export const criarFornecedorSchema = z
  .object({
    nome: z.string().trim().min(1, 'Informe o nome do fornecedor').max(200, 'O nome tem no máximo 200 caracteres').optional(),
    documento: documento.optional(),
    natureza: naturezaParceiroSchema.optional(),
    /** Parceiro ja existente que ganha o papel de fornecedor ("tambem e fornecedor"). */
    parceiroId: cuid.optional(),
    /** Sessao 15 (6b, P8): optante do Simples Nacional (so cadastro). */
    optanteSimples: z.boolean().optional(),
    ...dadosParceiroShape,
  })
  .superRefine((v, ctx) => {
    if (!v.nome && !v.parceiroId) ctx.addIssue({ code: 'custom', path: ['nome'], message: 'Informe o nome do fornecedor' });
    umPrincipal(v.enderecos ?? [], ctx);
  });
export type CriarFornecedorDto = z.infer<typeof criarFornecedorSchema>;

export const atualizarFornecedorSchema = z
  .object({
    nome: z.string().trim().min(1, 'Informe o nome do fornecedor').max(200, 'O nome tem no máximo 200 caracteres'),
    documento: documento.nullable(),
    natureza: naturezaParceiroSchema,
    ativo: z.boolean(),
    optanteSimples: z.boolean(),
    ...dadosParceiroAtualizarShape,
  })
  .partial()
  .superRefine((v, ctx) => umPrincipal(v.enderecos ?? [], ctx))
  .refine((v) => Object.keys(v).length > 0, 'Informe ao menos um campo');
export type AtualizarFornecedorDto = z.infer<typeof atualizarFornecedorSchema>;

export const listarFornecedoresSchema = paginacao.extend({
  q: z.string().trim().min(1).max(100).optional(),
  ativo: z
    .enum(['true', 'false'])
    .transform((v) => v === 'true')
    .optional(),
});
export type ListarFornecedoresQuery = z.infer<typeof listarFornecedoresSchema>;

export interface FornecedorResponse extends DadosParceiroResponse {
  /** Vem do Protheus (rotina em IMPORTAR): os campos de `CAMPOS_DO_PROTHEUS` sao somente leitura; a API recusa a edicao (409 CAMPO_DO_PROTHEUS). */
  doProtheus?: boolean;
  id: string;
  nome: string;
  documento: string | null;
  ativo: boolean;
  optanteSimples: boolean;
  createdAt: string;
  updatedAt: string;
}

/**
 * Formulario do fornecedor (tela Cadastros > Fornecedores): documento digitado com ou sem pontuacao; vazio = nao informado.
 * Saida: `documento` so com digitos ou `null`. Criar leva `documento` so quando informado (o POST nao aceita null).
 */
export const fornecedorFormSchema = z
  .object({
    nome: z.string().trim().min(1, 'Informe o nome do fornecedor').max(200, 'O nome tem no máximo 200 caracteres'),
    documento: z
      .string()
      .trim()
      .refine((v) => v === '' || /^(\d{11}|\d{14})$/.test(v.replace(/\D/g, '')), 'Documento: 11 dígitos (CPF) ou 14 (CNPJ)'),
    ativo: z.boolean(),
    optanteSimples: z.boolean().default(false),
  })
  .transform((v) => ({ nome: v.nome, documento: v.documento === '' ? null : v.documento.replace(/\D/g, ''), ativo: v.ativo, optanteSimples: v.optanteSimples }));
export type FornecedorFormEntrada = z.input<typeof fornecedorFormSchema>;
export type FornecedorFormSaida = z.output<typeof fornecedorFormSchema>;
